import * as React from "react";
import {
  Typography,
  Select,
  MenuItem,
  Box,
  TextField,
  createFilterOptions,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { caseContext } from "../../organisms/workItemsTable";
import {useCallback, useContext} from "react";
import ChevronUp from "../../../../public/assets/icons/chevron-up.svg";
import ChevronDown from "../../../../public/assets/icons/chevron-down.svg";
import reorder from "../../../../public/assets/icons/reorder.svg";
import deleteIcon from "../../../../public/assets/icons/delete.svg";
import lockIcon from "../../../../public/assets/icons/lock.svg";
import { GetAttributeReferenceData } from "../../../queries/GetAttributeReferenceData";
import theme from "../../../theme/Theme";
import debounce from "lodash/debounce";
import AutocompleteInput from "./atoms/AutocompleteInput";
import BetweenInput from "./molecules/BetweenInput";
import StartsWithInput from "./molecules/StartsWithInput";
import { isArrayWithNull } from "../../../utils/common";
import CustomAutoCompleteCheckbox from "../../atoms/customAutoCompleteCheckbox";
/*
   The FilterRow component is currently used in two locations.
   First in worklist (filters) and second in Coding Quality Rule Editor.
   Each of these components have different requirements for the filter row and use it in different ways.
   Key Notes:
    - Worklist currentFilter values are stored as an array of objects regardless of the filter type.
    - Coding Quality Rule Editor currentFilter values are stored as a string if not using 'in' operator.
    - Worklist filters are built from the detailGridOptions and use filters from the workitem
    - Coding Quality Rule Editor filters are built in CriteriaFilters and use custom filters NOT from the workitem
      1. You might see cases of billing_provider, performing_provider, assignee, and group. These are special cases for Coding Quality.
 */

//region Constants
type Props = {
  onFilterModified: any;
  filterIndex: number;
  uiIndex?: number; // Special prop for Coding Quality, used to determine if the join select should be displayed as we use hidden filters that can come first
  currentFilter: any;
  client: any;
  // setOptionsDict: any;
  // optionsDict: any;
  resetAll?: boolean;
  disabled?: boolean;
  setResetAll?: any;
  onFilterDelete?: any;
  isDetail?: boolean;
  allCoders?: any[];
  allGroups?: any[];
  nonManagerDetail?: boolean;
  worklist?: boolean;
  removable?: boolean;
  queues?: any[];
};

export const operators = [
  { value: "eq", label: "is" },
  // { value: "ne", label: "is not" },
  { value: "like", label: "like" },
  { value: "in", label: "in" },
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
];

const dateOperators = [
  { value: "eq", label: "is" },
  // { value: "ne", label: "is not" },
  { value: "gt", label: "after" },
  { value: "lt", label: "before" },
];

const codeOperators = [
    ...operators.filter(op =>
        op.value !== "gt" && op.value !== "lt"),
    { value: "startsWith", label: "Starts With" },
    { value: "bt", label: "Between" },
];

const numericOperators = [
  { value: "eq", label: "is" },
  { value: "in", label: "in" },
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
];

const numericFields = [
  "cmx_case_id",
];

export const joins = [
  { value: "AND", label: "and" },
  { value: "OR", label: "or" },
];

const dateFields = [
  "date_of_service",
  "admission_date",
  "discharge_date",
  "patient_dob",
  "sent_to_qa",
  "sent_for_review",
  "submitted_date"
]

const codeFields = [
    "procedure_code",
    "diagnosis_code"
]

const reverseSearchKeyMapper = {
  "procedure_code": "CPT Codes",
  "note_specialty": "Note Specialty",
  "patient_class": "Patient Class",
  "patient_type": "Patient Type",
  "modifier": "Modifiers",
  "diagnosis_code": "ICD Codes",
  "billing_area": "Billing Area",
  "facility": "Facility"
};
//endregion

//region Helper Functions
const searchValue = async (selectedDict, valueToSearch) => {
  if(!selectedDict) return valueToSearch;
  if(selectedDict.onSearch){
    // Run empty search to get all options and map to correct label based off of value
    const results = await selectedDict.onSearch(valueToSearch, true);
    const foundOption = results.find(option => option.value === valueToSearch);
    if (foundOption) {
      return foundOption
    }
    else {
      return valueToSearch;
    }
  } else {
    // Find the option in the pre-populated optionsDict and set the selected value
    const foundOption = selectedDict.find(option => option.value === valueToSearch);
    if(foundOption) {
      return foundOption;
    }
    else {
      return valueToSearch;
    }
  }
}
//endregion

export const FilterRow = ({
  onFilterModified,
  filterIndex,
  uiIndex,
  currentFilter,
  setResetAll,
  resetAll,
  onFilterDelete,
  client,
  isDetail,
  allCoders, // Special prop for Coding Quality
  allGroups, // Special prop for Coding Quality
  nonManagerDetail,
  disabled = false,
  worklist,
  queues
}: Props) => {

  //region Context
  const {
    directoryType,
    columnOptions,
    optionsDict,
    setOptionsDict,
    detailGridOptions
  } = useContext(caseContext);
  //endregion

  let openDict = {};

  let abortController = new AbortController();

  const buildValues = async (valueList, selectedFieldName = '') => {
    // Options Dynamic Search
    if(valueList?.onSearch){
      return;
    }

    setLoading(true);
    // Options Static
    const options = valueList
        .filter(val => val !== null || worklist)
        .map(async (value) => {
            let name = value;

            // null value case
            if (isArrayWithNull(value) || value === null) {
              return {
                label: 'EMPTY',
                id: value
              }
            }

            // Coder Special Case
            else if (selectedField === 'assignee' || selectedField === 'owner') {
              // Check for coder
              const coder = allCoders.find(coder => coder.data?.worker_id === value);
              name = coder ? `${coder.data?.first_name} ${coder.data?.last_name} (${coder.data?.username})` : value;

              // Check for group
              if (!coder) {
                const group = allGroups?.find(group => group.value === value);
                name = group ? group.label : value;
              }
            }
            // Group Special Case (CQA Filter)
            else if (selectedField === 'group') {
              const group = allGroups.find(group => group.value === value);
              name = group ? group.label : value;
            }
            // Worker group Special Case (distribution Filter)
            else if (selectedFieldName === 'worker_group') {
              const group = optionsDict['worker_group'].find(group => group.value === value);
              value = group ?? value;
            }
            // Worker queue Special Case (distribution Filter)
            else if (selectedFieldName === 'collection') {
              const queue = optionsDict['collection'].find(queue => queue.value === value);
              value = queue ?? value;
            }
            // QA Collection Special Case
            else if (selectedField === 'qa_collection_id') {
              const rule = optionsDict['qa_collection_id']?.find(val => val.value === value);
              value = rule ?? value;
            }
            else if (typeof value === 'boolean') {
                name = value ? 'True' : 'False';
            }
            else if(selectedField === 'profile') {
              const queue = queues?.find(queue => queue.value === value);
              value = queue ?? value;
            }
            // General Case
            else if (value?.label && value?.value) {
              return {
                label: value.label,
                id: value.value
              }
            }

            // Default (Map value to label)
            // Skip search for operators that don't use value-label structure
            const foundValue = ["like", "startsWith", "endsWith", "bt"].includes(currentFilter?.operator) ? value : await searchValue(optionsDict?.[selectedField] ?? optionsDict?.[reverseSearchKeyMapper[selectedField]], value);

            if(foundValue?.label) {
              return {
                label: foundValue?.label,
                id: foundValue?.value
              }
            }

            return {
              label: name ?? value,
              id: value
            }
        }) ?? [];
    const finalOptions = await Promise.all(options);

    setLoading(false);
    return finalOptions;
  }

  const buildColumns = (detailGridOptions: any) => {
    const columns = detailGridOptions?.columnDefs;
    let detailCells: any;
    if (detailGridOptions?.masterDetail) {
      detailCells =
        detailGridOptions?.detailCellRendererParams?.detailGridOptions;
    }
    const l: any = [];
    columns?.map((column) => {
      if (!column?.cellRenderer) {
        l.push(column);
      } else {
        openDict[column?.field] = false;
        l.push({
          ...column,
          children: buildColumns(detailCells),
        });
      }
    });
    return l;
  };

  //region State
  const [selectedField, setSelectedField] = React.useState(
    currentFilter?.fieldName ?? columnOptions?.[0]?.field
  );

  const [selectedFieldId, setSelectedFieldId] = React.useState(null);
  const [selectedOperator, setSelectedOperator] = React.useState(
    operators[0]?.value
  );
  const [operatorOptions, setOperatorOptions] = React.useState(() => {
    if (dateFields.includes(selectedField)) return dateOperators;
    if (codeFields.includes(selectedField)) return codeOperators;
    if (numericFields.includes(selectedField)) return numericOperators;
    if (selectedField === "patient_name") return [...operators, { value: "startsWith", label: "Starts With" }];
    return operators;
  });
  const [selectedJoin, setSelectedJoin] = React.useState(joins[0]?.value);
  const [values, setValues] = React.useState<any>([]);

  const [selectedValue, setSelectedValue] = React.useState<{
    label?: string,
    id?: string
  }[]>([]);
  const [textFieldValue, setTextFieldValue] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [headerNameAndValueDict, setHeaderNameAndValueDict] = React.useState(
    {}
  );
  const [text, _setText] = React.useState("");
  const [columns, setColumns] = React.useState(buildColumns(detailGridOptions));
  const [dropdownItems, setDropdownItems] = React.useState(<></>);
  //endregion

  //region Helpers
  const isEmptyOrAllUndefined = (arr: Array<string> | null | undefined) => {
    return Array.isArray(arr) && (arr.length === 0 || arr.every(item => item === undefined));
  }

  const onValueChange = async (event, inputValue) => {
    let newValue = inputValue;
    if (Array.isArray(newValue)) {
      newValue = newValue.map((val) => val?.id);
    } else {
      newValue = [newValue?.id];
    }

    // This supports a user as they use enter button instead of mouse click
    if(!newValue || isEmptyOrAllUndefined(newValue)) {
      newValue = [inputValue];
    }

    if (
      newValue &&
      (selectedValue?.join(",") !== newValue?.join(",") ||
        isArrayWithNull(newValue))
    ) {
      buildValues(newValue).then((values) => {
        setSelectedValue(values);
        inputValue !== null && onFilterModified(
          {
            ...currentFilter,
            filterIndex: filterIndex,
            fieldId: selectedFieldId,
            fieldName: selectedField,
            operator: selectedOperator,
            value: newValue,
            join: filterIndex !== 0 ? selectedJoin : undefined,
          },
          true
        );
      });
    }
  };

  const onFieldNameChanged = (e, child) => {
    setSelectedField(e.target.value);
    setSelectedFieldId(child?.props?.id);
    if (dateFields.includes(e.target.value)) {
      setOperatorOptions(dateOperators);
    } else if (codeFields.includes(e.target.value)) {
      setOperatorOptions(codeOperators);
    } else if (numericFields.includes(e.target.value)) {
      setOperatorOptions(numericOperators);
    } else if (e.target.value === "patient_name") {
        setOperatorOptions([...operators, { value: "startsWith", label: "Starts With" }]);
    } else {
      setOperatorOptions(operators);
    }

    onFilterModified({
      ...currentFilter,
      filterIndex: filterIndex,
      fieldId: child?.props?.id,
      fieldName: e.target.value,
      operator: selectedOperator,
      value: undefined,
      join: filterIndex !== 0 ? selectedJoin : undefined,
    });

    setSelectedValue([]);
  };

  const getReferenceData = (
    attributeId: string,
    searchTerm: string | number = ""
  ) => {
    if (attributeId && !["owner", "group", "assignee", "collection", "worker_group", "qa_collection_id", "profile"].includes(attributeId) && !Object.keys(reverseSearchKeyMapper).includes(selectedField)) {
      abortController.abort();
      abortController = new AbortController();
      client
        ?.query({
          query: GetAttributeReferenceData,
          context: {
            fetchOptions: {
              signal: abortController.signal,
            },
          },
          variables: {
            entityType:
              directoryType === "distribution" ? "worker" : directoryType,
            attributeId,
            searchTerm,
          },
          fetchPolicy: "no-cache",
        })
        .then((response: any) => {
          console.log("filter row getReferenceData response: ",response);
          const data = response?.data?.GetAttributeReferenceData;
          buildValues(data).then((values) => {
            setValues(values);
            setLoading(false);
          });
        })
        .catch((err) => {
          console.error(err.message);
          setLoading(false);
        });
    }
  };

  const handleTextFieldChange = (e) => {
    setTextFieldValue(e.target.value ?? "");
    onFilterModified(
      {
        ...currentFilter,
        filterIndex: filterIndex,
        fieldId: selectedFieldId,
        fieldName: selectedField,
        operator: selectedOperator,
        value: e.target.value,
        join: filterIndex !== 0 ? selectedJoin : undefined,
      },
      true
    );
  };

  const onOperatorChanged = (e) => {
    let value = (selectedValue?.length || !textFieldValue?.length)
      ? selectedValue.filter(val => val.id !== undefined).map(val => val.id)
      : [textFieldValue];
    if(Array.isArray(value) && value.length === 0 && selectedValue?.length > 0) setSelectedValue([]);
    const valueIsNotNull = value.every(val => val !== undefined);
    setSelectedOperator(e.target.value);
    onFilterModified(
      {
        ...currentFilter,
        filterIndex: filterIndex,
        fieldId: selectedFieldId,
        fieldName: selectedField,
        operator: e.target.value,
        value: value,
        join: filterIndex !== 0 ? selectedJoin : undefined,
      },
      selectedField && valueIsNotNull && value?.length
    );
  };

  const onJoinChanged = (e) => {
    setSelectedJoin(e.target.value);
    onFilterModified(
      {
        ...currentFilter,
        filterIndex: filterIndex,
        fieldId: selectedFieldId,
        fieldName: selectedField,
        operator: selectedOperator,
        value: selectedValue.map(val => val.id),
        join: e.target.value,
      },
      selectedField && selectedOperator && selectedValue?.length
    );
  };

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 500,
    ignoreCase: true,
  });

  const handleClick = (fieldName) => {
    openDict[fieldName] = !openDict[fieldName];
    const l = getDropdownValues(columns, openDict);
    setDropdownItems(l);
  };

  const getFieldName = (value, dict) => {
    if (value?.includes(".")) {
      const split = value.split(".");
      const last = split[split.length - 1];

      if(dict?.[last]) {
        return split.slice(0, -1).join(".") + "." + dict?.[last];
      } else {
        return value;
      }
    }
    return (dict?.[value] ?? value);
  };

  const searchDebounce = useCallback(
    debounce(async (value) => {
      try {
        if(optionsDict?.[selectedFieldId]?.onSearch) {
          setLoading(true);
          const results = await optionsDict?.[selectedFieldId]?.onSearch(value) ?? [];

          buildValues(results)
              .then((values) => {
                setValues(values);
                setLoading(false);
              });
        } else if(value === '' || value?.length > 1) {
          setLoading(true);
          getReferenceData(selectedFieldId, value);
          setLoading(false);
        }
      }
      catch (e) {
        setLoading(false);
      }
    }, 700),
    [optionsDict, selectedFieldId, setLoading, setValues]
  );
  //endregion

  //region Lifecycle
  React.useEffect(() => {
    if (resetAll) {
      setSelectedOperator(operators[0]?.value);
      setSelectedField(columnOptions?.[0]?.field);
      if (dateFields.includes(columnOptions?.[0]?.field)) {
        setOperatorOptions(dateOperators)
      }
      else if (codeFields.includes(columnOptions?.[0]?.field)) {
        setOperatorOptions(codeOperators)
      }
      setSelectedValue([]);
      setResetAll(false);
    }
  }, [resetAll]);
  React.useEffect(() => {
    const updateFilter = async () => {
      if (currentFilter && currentFilter?.fieldId !== selectedFieldId) {
        setSelectedField(currentFilter?.fieldName);
        setSelectedFieldId(currentFilter?.fieldId);
        setSelectedOperator(currentFilter?.operator);

        // Remove all %, for (like, starts, ends) operators
        if (
            currentFilter.value &&
            ["like", "startsWith", "endsWith"].includes(currentFilter?.operator) &&
            typeof currentFilter.value === "string"
        ) {
          currentFilter.value = currentFilter.value.replace(/%/g, "");
        }

        // Value Formatting (i.e. filter contains ID, but we want to display name)
        let value = currentFilter?.value;
        let selectedField = currentFilter?.fieldName;

        // TODO: Refactor to remove allCoders & allGroups and just use the "Initial Value Setting" below
        if ((currentFilter.fieldName === "assignee" || currentFilter.fieldName === "owner") && value && currentFilter?.operator !== "in") {
          // Check for coder
          const coder = allCoders.find(coder => coder.data?.worker_id === value);
          value = coder ? coder.data?.first_name + ' ' + coder.data?.last_name : value;

          // Check for group
          if(!coder) {
            const group = allGroups?.find(group => group.value === value);
            value = group ? group.label : value;
          }
        }
        // Group Special Case (CQA Filter)
        else if(currentFilter.fieldName === "group" && value && currentFilter?.operator !== "in") {
          const group = allGroups.find(group => group.value === value)
          value = group ? group.label : value;
        }
        else if(currentFilter.fieldName === "qa_collection_id" && value && currentFilter?.operator !== "in") {
          const rule = optionsDict['qa_collection_id'].find(rule => rule.value === value)
          value = rule ?? value;
        }

        // Initial Value Setting
        buildValues(Array.isArray(value) ? value : [value], currentFilter?.fieldName)
            .then((values) => {
                setSelectedValue(values);
            });

        if (value?.label)
          setTextFieldValue(value.label);
        else
          setTextFieldValue(value);

        setSelectedJoin(currentFilter?.join);

        if (optionsDict?.[currentFilter?.fieldId]?.onSearch)
          searchDebounce("");
        else {
          console.log("filter row  optionsDict: ",optionsDict);

          buildValues(optionsDict?.[currentFilter?.fieldId] ?? [])
                .then((values) => {
                  console.log("filter row buildValues results: ",values);

                    setValues(values);
                });
        }
      }
    }

    const update = async () => {
      setLoading(true);
      await updateFilter();
      setLoading(false);
    }

    update();
  }, [currentFilter, allCoders, allGroups]);
  React.useEffect(() => {
    const columnDefs = buildColumns(detailGridOptions);
    setColumns(columnDefs);
    const l = getDropdownValues(columnDefs, openDict);
    setDropdownItems(l);
  }, [detailGridOptions]);
  React.useEffect(() => {
    if(selectedFieldId) {
      if (optionsDict[selectedFieldId]?.onSearch) {
        console.log("filter row selectedFieldId optionsDict on search: ",optionsDict);

        setValues([]);
        searchDebounce("");
      }
      else if (optionsDict[selectedFieldId]?.length) {
        console.log("filter row selectedFieldId optionsDict length: ",optionsDict);

        buildValues((optionsDict[selectedFieldId] ?? []))
            .then((values) => {
                setValues(values);
            });
      } 
      else {
        setLoading(true);
        getReferenceData(selectedFieldId);
        setLoading(false);
      }
    }
  }, [selectedFieldId]);
  //endregion

  const getDropdownValues = (
    columns: any,
    openDict: any,
    paddingLeft?: number,
    parent?: string,
    dontDisplay?: boolean
  ) => {
    console.log("getDropdownValues : ", columns);
    let leftPadding = paddingLeft ?? 16;
    let l: any = [];
    columns?.map((column) => {
      dontDisplay = column.selected;
      const parentToNext = parent
        ? `${parent}.${column?.field}`
        : column?.field;
      const temp = headerNameAndValueDict;
      temp[parentToNext] = column?.headerName;
      setHeaderNameAndValueDict(temp);

      if (!column?.cellRenderer) {
        l.push(
          <MenuItem
            key={parentToNext}
            id={column?.id}
            value={parentToNext}
            itemType={column?.entityType}
            sx={{
              padding: `8px 16px 8px ${leftPadding}px`,
              display: dontDisplay ? "none" : "flex",
            }}
          >
            {column?.headerName}
          </MenuItem>
        );
      } else {
        l.push(
          <MenuItem
            key={column?.field}
            value={column?.field}
            onClickCapture={(e) => {
              e.stopPropagation();
              handleClick(column?.field);
            }}
            sx={{
              display: dontDisplay ? "none" : "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography>{column?.headerName}</Typography>
            <img
              src={openDict?.[column?.field] ? ChevronUp : ChevronDown}
              alt={openDict?.[column?.field] ? "expandless" : "expandmore"}
              style={{ height: "20px" }}
            />
          </MenuItem>
        );
        let noDisplay;
        if (openDict[column?.field]) {
          noDisplay = false;
        } else {
          noDisplay = true;
        }
        const childItems = getDropdownValues(
          column?.children,
          openDict,
          leftPadding + 20,
          parentToNext,
          noDisplay
        );
        l = [...l, ...childItems];
      }
    });
    return l;
  };

  const getOptionsSelect = () => {
    if (dateFields.includes(selectedField)) {
      return (
        <input
          disabled={(currentFilter?.isValueStatic || disabled)}
          type="date"
          
          value={textFieldValue}
          onChange={handleTextFieldChange}
          style={{
            border: `1px solid ${theme.palette.artisticStroke200.main}`,
            height: '36px',
            paddingLeft: '8px',
            paddingRight: '8px'
          }} />
      );
    } else if (selectedOperator === "like") {
      return (
        <TextField
          disabled={(currentFilter?.isValueStatic || disabled)}
          sx={{
            width: 300,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0
            },
          }}
          size="small"
          placeholder="Enter text"
          value={textFieldValue}
          onChange={handleTextFieldChange}
        />
      );
    } else if (selectedOperator === "bt") {
      return (
        <BetweenInput
          data-testid="between-input"
          disabled={(currentFilter?.isValueStatic || disabled)}
          values={values}
          selectedValue={selectedValue}
          selectedOperator={selectedOperator}
          selectedField={selectedField}
          selectedFieldId={selectedFieldId}
          setSelectedFieldId={setSelectedFieldId}
          filterOptions={filterOptions}
          optionsDict={optionsDict}
          columnOptions={columnOptions}
          onValueChange={onValueChange}
          textFieldValue={textFieldValue}
          handleTextFieldChange={handleTextFieldChange}
          searchDebounce={searchDebounce}
          loading={loading}
          nonManagerDetail={nonManagerDetail}
          text={text}
          setText={_setText}
        />
      )
    } else if (selectedOperator === "startsWith") {
      return (
        <StartsWithInput
          data-testid="starts-with-input"
          disabled={(currentFilter?.isValueStatic || disabled)}
          values={values}
          selectedValue={selectedValue}
          selectedOperator={selectedOperator}
          selectedField={selectedField}
          selectedFieldId={selectedFieldId}
          setSelectedFieldId={setSelectedFieldId}
          filterOptions={filterOptions}
          optionsDict={optionsDict}
          columnOptions={columnOptions}
          onValueChange={onValueChange}
          textFieldValue={textFieldValue}
          handleTextFieldChange={handleTextFieldChange}
          searchDebounce={searchDebounce}
          loading={loading}
          nonManagerDetail={nonManagerDetail}
          text={text}
          setText={_setText}
        />
      )
    }  else if (selectedOperator === "in") {
            return (
        <CustomAutoCompleteCheckbox
          disabled={(currentFilter?.isValueStatic || disabled)}
          setValues={(values) => {
            buildValues(values)
                .then((values) => {
                  setValues(values);
                });
          }}
          values={(worklist && ["eq", "ne"]?.includes(selectedOperator) ? !values?.find(val => val?.label === 'EMPTY') ? [{label: 'EMPTY', id: null}, ...values] : values : values?.filter(v => v?.id))}
          selectedValue={selectedValue}
          selectedOperator={selectedOperator}
          selectedField={selectedField}
          selectedFieldId={selectedFieldId}
          setSelectedFieldId={setSelectedFieldId}
          filterOptions={filterOptions}
          optionsDict={optionsDict}
          columnOptions={columnOptions}
          onValueChange={onValueChange}
          textFieldValue={textFieldValue}
          handleTextFieldChange={handleTextFieldChange}
          searchDebounce={searchDebounce}
          loading={loading}
          nonManagerDetail={nonManagerDetail}
          text={text}
          setText={_setText}
        />
      );
    } else {
      return (
        <AutocompleteInput
          disabled={(currentFilter?.isValueStatic || disabled)}
          setValues={(values) => {
            buildValues(values)
                .then((values) => {
                  setValues(values);
                });
          }}
          values={(worklist && ["eq", "ne"]?.includes(selectedOperator) ? !values?.find(val => val?.label === 'EMPTY') ? [{label: 'EMPTY', id: null}, ...values] : values : values?.filter(v => v?.id))}
          selectedValue={selectedValue}
          selectedOperator={selectedOperator}
          selectedField={selectedField}
          selectedFieldId={selectedFieldId}
          setSelectedFieldId={setSelectedFieldId}
          filterOptions={filterOptions}
          optionsDict={optionsDict}
          columnOptions={columnOptions}
          onValueChange={onValueChange}
          textFieldValue={textFieldValue}
          handleTextFieldChange={handleTextFieldChange}
          searchDebounce={searchDebounce}
          loading={loading}
          nonManagerDetail={nonManagerDetail}
          text={text}
          setText={_setText}
        />
      );
    }
  }

  return (
    <Box sx={{ display: "grid", gridAutoFlow: "column", alignItems: "center", cursor: "grab"}}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          height: "37px",
          width: "30px",
          cursor: "grab",
          ml: "-12px",
        }}
      >
        <img src={reorder} alt="reorder" />
      </Box>
      {(filterIndex === 0 || uiIndex === 0) ? (
        <Typography
          variant="body1"
          sx={{
            color: "artisticTextHigh.main",
            width: "70px",
            textAlign: "start",
            display: "flex",
            justifyContent: "center"
          }}
        >
          Where
        </Typography>
      ) : (
        <Select
          value={selectedJoin?.length ? selectedJoin : "AND"}
          defaultValue={""}
          onChange={onJoinChanged}
          sx={{
            height: "37px",
            width: "70px",
            "& .MuiSelect-icon": {
              backgroundImage: `url(${ChevronDown})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              color: "transparent",
            },
            "& .MuiSelect-select": {
              paddingRight: "0px !important",
            },
          }}
          disabled={nonManagerDetail || disabled}
        >
          {joins.map((join) => (
            <MenuItem
              disabled={disabled}
              key={join?.value}
              value={join?.value}
              sx={{ padding: "8px 16px" }}
            >
              {join?.label}
            </MenuItem>
          ))}
        </Select>
      )}
      <Box width="10px" />
      <Tooltip
        title={getFieldName(selectedField, headerNameAndValueDict)}
        placement="bottom-end"
      >
        <Select
          id="fieldNameSelector"
          data-testid="fieldNameSelector"
          value={selectedField}
          defaultValue={""}
          onChange={(e, child) => {
            if (openDict[e.target.value] === undefined) {
              onFieldNameChanged(e, child);
            }
          }}
          renderValue={(value) => getFieldName(value, headerNameAndValueDict)}
          sx={{
            minHeight: "37px",
            height: "100%",
            width: "200px",
            paddingRight: "8px",
            "& .MuiSelect-icon": {
              backgroundImage: `url(${ChevronDown})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              color: "transparent",
            },
            "& .MuiSelect-select": {
              padding: "0 16px !important",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${theme.palette.artisticStroke200.main} !important`,
            },
            "&.Mui-focused fieldset": {
              border: `1px solid ${theme.palette.artisticGrey400.main} !important`,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: `${theme.palette.artisticGrey400.main} !important`,
            },
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
          MenuProps={{
            PaperProps:
                { sx: {
                    maxHeight: 300,
                    "& .MuiList-root": {
                      width: "100%",
                    },
                  }
                }
          }}
          native={false}
          displayEmpty={true}
          disabled={nonManagerDetail || (currentFilter?.isFieldStatic || disabled)}
        >
          {dropdownItems}
        </Select>
      </Tooltip>
      <Select
        id="operatorNameSelector"
        data-testid="operatorNameSelector"
        value={selectedOperator}
        defaultValue={""}
        onChange={onOperatorChanged}
        disabled={nonManagerDetail || (currentFilter?.isOperatorStatic || disabled)}
        sx={{
          minHeight: "37px",
          height: "100%",
          width: "100px",
          paddingRight: "8px",
          borderRadius: 0,
          "& .MuiSelect-select": {
            padding: "0 16px !important",
          },
          "& .MuiSelect-icon": {
            backgroundImage: `url(${ChevronDown})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            color: "transparent",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${theme.palette.artisticStroke200.main} !important`,
            borderLeft: "none !important",
            borderRight: "none !important",
          },
          "&.Mui-focused fieldset": {
            border: `1px solid ${theme.palette.artisticGrey400.main} !important`,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: `${theme.palette.artisticGrey400.main} !important`,
          },
        }}
      >
        {operatorOptions.map((operator) => (
          <MenuItem
            disabled={disabled}
            key={operator?.value}
            value={operator?.value}
            sx={{ padding: "8px 16px" }}
          >
            {operator?.label}
          </MenuItem>
        ))}
      </Select>

      {getOptionsSelect()}

      {!nonManagerDetail ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: "37px",
            height: "100%",
            width: "30px",
            borderTopRightRadius: 6,
            borderBottomRightRadius: 6,
            border: `1px solid ${theme.palette.artisticStroke200.main}`,
            borderLeft: "none",
          }}
        >
          {(currentFilter?.static ?? false) ? (
            <Tooltip title={"This filter is not removable!"} placement="top">
              <img src={lockIcon} alt="info" style={{width: "25px", height: "auto", cursor: "default"}} />
            </Tooltip>
          ) : (
            <Button
              onClick={() => onFilterDelete(filterIndex)}
              sx={{
                minWidth: "30px",
              }}
              disableRipple={true}
              disabled={nonManagerDetail || disabled}
            >
              <img src={deleteIcon} alt="delete" />
            </Button>
          )}
        </Box>
      ) : null}
    </Box>
  );
};
